import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import dayjs from "dayjs";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { Visibility as VisibilityIcon } from "@mui/icons-material";
import { Button, IconButton, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";
import { PageContainer } from "@/features/ui";

import { useAuth } from "@/features/authentication";
import {
  AllOutletsSelect,
  OutletRoleEnum,
  UserOutletsSelect,
} from "@/features/outlets";
import { PermissionEnum } from "@/features/permissions";
import { apiOutletClient, formatDate, formatNumber, getErrorMessage } from "@/features/shared";
import { SupplierSelect } from "@/features/suppliers";
import {
  CustomDateRangePicker,
  SearchTextField,
  useSnackbar,
} from "@/features/ui";
import {
  useDateRangeFilter,
  useSearchFilter,
  useServerPagination,
} from "@/hooks";

export const PurchaseReturns = () => {
  const { outlet, changeOutlet, hasPermission } = useAuth();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const [purchaseReturns, setPurchaseReturns] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    setRowCount,
    paginationParams,
    dataGridPaginationProps,
    resetParams,
  } = useServerPagination();
  const { keyword, searchProps } = useSearchFilter({ resetParams });
  const { dateRangeProps, dateParams } = useDateRangeFilter({ resetParams });

  const prevOutletRoleRef = useRef();

  // Parse seller from URL params
  const seller = useMemo(() => {
    const sellerParam = searchParams.get("seller");
    if (!sellerParam) return null;
    try {
      return JSON.parse(sellerParam);
    } catch {
      return null;
    }
  }, [searchParams]);

  const loadPurchaseReturns = useCallback(async () => {
    if (!outlet) return;

    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await apiOutletClient.get("purchase-returns", {
        params: {
          ...paginationParams,
          ...dateParams,
          ...(keyword && { keyword }),
          sellerableId: seller?.id || null,
          withSeller: true,
          withCreator: true,
        },
      });
      setPurchaseReturns(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat retur pembelian"));
    } finally {
      setLoading(false);
    }
  }, [
    outlet,
    paginationParams,
    dateParams,
    keyword,
    seller,
    setRowCount,
    showSnackbar,
  ]);

  useEffect(() => {
    loadPurchaseReturns();
  }, [loadPurchaseReturns]);

  // Reset seller when outlet role changes
  useEffect(() => {
    if (
      prevOutletRoleRef.current !== undefined &&
      prevOutletRoleRef.current !== outlet?.role
    ) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("seller");
        return newParams;
      });
    }
    prevOutletRoleRef.current = outlet?.role;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outlet?.role]);

  const handleSellerChange = (value) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (value) {
        newParams.set(
          "seller",
          JSON.stringify({ id: value.id, name: value.name })
        );
      } else {
        newParams.delete("seller");
      }
      resetParams.forEach((param) => newParams.delete(param));
      return newParams;
    });
  };

  const toolbarContent = hasPermission(
    PermissionEnum.CREATE_PURCHASE_RETURNS
  ) && (
    <Button variant="contained" component={Link} to="create">
      Tambah
    </Button>
  );

  return (
    <PageContainer
      breadcrumbs={[]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
      maxWidth={false}
    >
      <Stack spacing={3}>
        <Grid container spacing={2} sx={{ justifyContent: "flex-end", alignItems: "end" }}>
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            {outlet?.role === OutletRoleEnum.HUB ? (
              <SupplierSelect
                value={seller}
                onChange={handleSellerChange}
                TextFieldProps={{
                  label: "Pemasok (Supplier)",
                }}
              />
            ) : (
              <AllOutletsSelect
                value={seller}
                onChange={handleSellerChange}
                allowedRoles={[OutletRoleEnum.HUB]}
                TextFieldProps={{
                  label: "Pemasok (Hub)",
                }}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <UserOutletsSelect
              value={outlet}
              onChange={changeOutlet}
              TextFieldProps={{
                label: "Pembeli",
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <SearchTextField
              fullWidth
              placeholder="Cari No. Referensi"
              {...searchProps}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 12, lg: 6 }}>
            <CustomDateRangePicker {...dateRangeProps} />
          </Grid>
        </Grid>
        <DataGrid
          rows={purchaseReturns}
          loading={loading}
          getRowHeight={() => "auto"}
          columns={[
            {
              field: "referenceNumber",
              headerName: "Nomor #",
              flex: 1,
              sortable: false,
            },
            {
              field: "transactionDate",
              headerName: "Tanggal",
              flex: 1,
              sortable: false,
              valueGetter: (transactionDate) => {
                const today = dayjs().startOf("day");
                const transaction = dayjs(transactionDate).startOf("day");
                const age = today.diff(transaction, "day");
                return age > 0
                  ? `${formatDate(transactionDate)} (${age}d)`
                  : formatDate(transactionDate);
              },
            },
            {
              field: "creator",
              headerName: "Dibuat Oleh",
              flex: 1,
              sortable: false,
              valueGetter: (creator) => creator?.name ?? "-",
            },
            {
              field: "seller",
              headerName: "Pemasok",
              flex: 1.5,
              sortable: false,
              valueGetter: (seller) => seller?.name ?? "-",
            },
            {
              field: "note",
              headerName: "Catatan",
              flex: 1,
              sortable: false,
              valueGetter: (note) => note ?? "-",
            },
            {
              field: "grandTotal",
              headerName: "Total",
              flex: 1,
              type: "number",
              sortable: false,
              valueGetter: (grandTotal) => formatNumber(grandTotal),
            },
            {
              field: "actions",
              type: "actions",
              headerName: "Aksi",
              getActions: ({ row }) => {
                const actions = [];
                if (hasPermission(PermissionEnum.READ_PURCHASE_RETURNS)) {
                  actions.push(
                    <IconButton
                      key="view"
                      onClick={() => navigate(row.referenceNumber)}
                      size="small"
                    >
                      <VisibilityIcon />
                    </IconButton>
                  );
                }
                return actions;
              },
            },
          ]}
          {...dataGridPaginationProps}
          disableRowSelectionOnClick
        />
      </Stack>
    </PageContainer>
  );
};
